<template>
    <div class="flex flex-col h-full" style="max-width: 100%;">
        <div class="w-full building-title-bar border-b-2 border-teal-700 shadow-lg py-3">
            <div class="mx-auto container flex items-center justify-between w-full">
                <div class="flex items-center">
                    <div class="group flex items-center gap-x-5 text-white leading-normal">
                        <div class="flex flex-col">
                            <div class="font-semibold uppercase text-xl">{{ selectedProposal.building.address_line_1 }}</div>
                            <div class="uppercase text-sm">{{ selectedProposal.building.city }}, {{ selectedProposal.building.state }}</div>
                        </div>
                        <div class="text-2xl">|</div>
                        <div class="text-2xl text-white font-medium">
                            Revision #{{ selectedProposal.revision_number }}
                        </div>
                    </div>
                </div>
                <div class="flex items-center gap-x-3">
                    <a :href="`/projects/${selectedProposal.project_id}`" class="flex items-center gap-x-2 btn btn-small btn-green">
                        <inline-svg name="chevron-left" classes="w-5 h-5 text-white fill-current" />
                        Back to Project
                    </a>
                    <button v-if="canCreateRevision" class="btn btn-small btn-orange min-w-[10rem]" @click.prevent="createRevision">
                        <div class="flex items-center justify-center">
                            <div v-if="loading">
                                <inline-svg name="loading-button"></inline-svg>                                
                            </div>
                            <div v-else class="flex items-center">
                                <inline-svg name="add" classes="w-3 h-3 text-white fill-current" />
                                <span class="ml-2">New Revision</span>
                            </div>
                        </div>
                    </button>
                    <button v-if="canUpdateProposal" class="btn btn-small btn-green min-w-[10rem]" @click.prevent="saveProposal">
                        <div class="flex items-center justify-center">
                            <div v-if="loading">
                                <inline-svg name="loading-button"></inline-svg>                                
                            </div>
                            <div v-else class="flex items-center">
                                <inline-svg name="save" classes="w-3 h-3 text-white fill-current" />
                                <span class="ml-2">Save Proposal</span>
                            </div>
                        </div>
                    </button>
                </div>
            </div>
        </div>

        <div id="proposal-container" class="flex-1 overflow-y-auto">
            <div class="py-5 mx-auto container">
                <a name="pictures"></a>
                <div class="flex flex-col">
                    <div class="flex">
                        <div class="flex flex-col max-w-[540px] overflow-hidden">
                            <media-gallery
                                :documentable-id="selectedProposal.building.id"
                                documentable-type="App\Building"
                                :media="selectedProposal.building.media"
                                :height="340"
                                :show-media-manager="false">
                            </media-gallery>
                        </div>
                        <div class="ml-3 flex flex-col">
                            <div class="flex gap-x-3">
                                <table class="shadow-none text-sm border border-gray-300">
                                    <tbody>
                                        <tr>
                                            <td class="pl-4 py-1 font-semibold">Rentable Sq. Ft.</td>
                                            <td class="py-1">
                                                <number-input name="total_square_feet" :size="10" v-model="selectedProposal.total_square_feet" classes="text-sm text-right py-1" justify-end></number-input>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td class="pl-4 py-1 font-semibold">Loss Factor (%)</td>
                                            <td class="py-1">
                                                <percent-input name="loss_factor_percentage" :size="5" v-model="selectedProposal.loss_factor_percentage" add-classes="text-sm text-right py-1" justify-end></percent-input>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td class="pl-4 py-1 font-semibold">Usable Sq. Ft.</td>
                                            <td class="py-1 text-sm text-right font-medium">
                                                <span class="pr-2">{{ usableSqFt | number }}</span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td class="pl-4 py-1 font-semibold">Base Rent ($)</td>
                                            <td class="py-1 text-sm text-right font-medium">
                                                <span class="pr-2">{{ formatCurrency(baseRent, 2) }}</span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td class="pl-4 py-1 font-semibold">Term (Years)</td>
                                            <td class="py-1">
                                                <number-input name="term" :size="5" v-model="selectedProposal.term" classes="text-sm text-right py-1" justify-end></number-input>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td class="pl-4 py-1 font-semibold"># Free Rent Months</td>
                                            <td class="py-1 text-sm text-right font-medium">
                                                <span class="pr-2">{{ totalNumberFreeMonths }}</span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td class="pl-4 py-1 font-semibold">Months in Term</td>
                                            <td class="py-1 text-sm text-right font-medium">
                                                <span class="pr-2">{{ monthsInTerm }}</span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td class="pl-4 py-1 font-semibold">Incl. Free Months</td>
                                            <td class="py-1 flex justify-end text-sm text-right font-medium">
                                                <div class="pr-2 flex items-center">
                                                    <input type="checkbox" class="w-5 h-5" v-model="selectedProposal.include_free_months" />
                                                    <span class="ml-2">{{ selectedProposal.include_free_months ? 'Yes' : 'No' }}</span>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                <table class="shadow-none text-sm border border-gray-300">
                                    <tbody>
                                        <tr>
                                            <td class="pl-4 py-1 font-semibold">Total Rent</td>
                                            <td class="pl-10 py-1 text-sm text-right font-medium">
                                                <span class="pr-2">{{ formatCurrency(totalRent, 0) }}</span>
                                            </td>
                                            <td class="pl-10 py-1 font-semibold">Annual Avg $</td>
                                            <td class="pl-10 py-1 text-sm text-right font-medium">
                                                <span class="pr-2">{{ formatCurrency(annualAverageCost, 0) }}</span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td class="pl-4 py-1 font-semibold">Total Op. Ex.</td>
                                            <td class="py-1 text-sm text-right font-medium">
                                                <span class="pr-2">{{ formatCurrency(totalOperatingExpenses, 0) }}</span>
                                            </td>
                                            <td class="pl-10 py-1 font-semibold">Monthly Avg $</td>
                                            <td class="py-1 text-sm text-right font-medium">
                                                <span class="pr-2">{{ formatCurrency(monthlyAverageCost, 0) }}</span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td class="pl-4 py-1 font-semibold">Total Lease $</td>
                                            <td class="py-1 text-sm text-right font-medium">
                                                <span class="pr-2">{{ formatCurrency(totalLeaseCost, 0) }}</span>
                                            </td>
                                            <td class="pl-10 py-1 font-semibold">Eff. Rent PSF</td>
                                            <td class="py-1 text-sm text-right font-medium">
                                                <span class="pr-2">{{ formatCurrency(effectiveRentPSF, 2) }}</span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td class="pl-4 py-1 font-semibold">Discount Rate</td>
                                            <td class="py-1">
                                                <percent-input name="discount_rate" :size="5" v-model="selectedProposal.discount_rate" add-classes="text-sm text-right py-1" justify-end></percent-input>
                                            </td>
                                            <td class="pl-10 py-1 font-semibold">Average PUSF</td>
                                            <td class="py-1 text-sm text-right font-medium">
                                                <span class="pr-2">{{ formatCurrency(averagePUSF, 2) }}</span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td class="pl-4 py-1 font-semibold">NPV</td>
                                            <td class="py-1 text-sm text-right font-medium">
                                                <span class="pr-2">{{ formatCurrency(npv, 0) }}</span>
                                            </td>
                                            <td colspan="2"></td>
                                        </tr>
                                        <tr>
                                            <td class="pl-4 py-1 font-semibold">TI $ Req. PSF</td>
                                            <td class="py-1">
                                                <currency-input name="ti_required_psf" :size="6" v-model="selectedProposal.ti_required_psf" add-classes="text-sm text-right py-1" is-decimal justify-end></currency-input>
                                            </td>
                                            <td colspan="2"></td>
                                        </tr>
                                        <tr>
                                            <td class="pl-4 py-1 font-semibold">TI $ Offered PSF</td>
                                            <td class="py-1">
                                                <currency-input name="ti_offered_psf" :size="6" v-model="selectedProposal.ti_offered_psf" add-classes="text-sm text-right py-1" is-decimal justify-end></currency-input>
                                            </td>
                                            <td colspan="2"></td>
                                        </tr>
                                        <tr>
                                            <td class="pl-4 py-1 font-semibold">TI $ to Amort.</td>
                                            <td class="py-1 text-sm text-right font-medium">
                                                <span class="pr-2">{{ formatCurrency(tiToAmortization, 2) }}</span>
                                            </td>
                                            <td colspan="2"></td>
                                        </tr>
                                        <tr>
                                            <td class="pl-4 py-1 font-semibold">TI $ Tenant</td>
                                            <td class="pl-10 py-1 text-sm text-right font-medium">
                                                <span class="pr-2">{{ formatCurrency(tiTenantCost, 0) }}</span>
                                            </td>
                                            <td colspan="2"></td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <div class="mt-5 overflow-x-auto">
                        <table class="w-full bordered">
                            <thead>
                                <tr>
                                    <th></th>
                                    <th class="text-center">Years</th>
                                    <th v-for="(row, rowIndex) in termData" :key="rowIndex" class="text-center">{{ row.year }}</th>
                                    <th class="text-right">Totals</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td class="py-1">
                                        <div class="flex items-center">
                                            <span class="font-bold">Expense</span>
                                            <button v-if="canCreateExpense" type="button" @click="$modal.show('new-proposal-expense')">
                                                <inline-svg name="add" classes="ml-2 w-4 h-4 text-green-500 hover:text-green-300 fill-current cursor-pointer"></inline-svg>
                                            </button>
                                        </div>
                                    </td>
                                    <td class="text-right font-semibold">Free Months</td>
                                    <td v-for="(row, rowIndex) in termData" :key="rowIndex">
                                        <select @change="saveFreeMonths" v-model="row.number_free_months" :data-year="row.year" class="mx-auto mt-0 max-w-fit form-select text-center text-xs">
                                            <option value="0">0</option>
                                            <option v-for="i in 12" :key="i" :value="i">{{ i }}</option>
                                        </select>
                                    </td>
                                    <td></td>
                                </tr>
                                <tr>
                                    <td class="bg-blue-500 text-white font-bold">Totals</td>
                                    <td class="bg-blue-500 text-white font-semibold"></td>
                                    <td class="bg-blue-500 text-white text-right font-semibold" v-for="(row, rowIndex) in termData" :key="rowIndex">{{ formatCurrency(row.total, 0) }}</td>
                                    <td class="bg-blue-500 text-white text-right font-semibold">{{ formatCurrency(totalLeaseCost, 0) }}</td>
                                </tr>
                            </tbody>
                            <tbody v-for="expense in expenses" :key="expense.id">
                                <tr>
                                    <td>
                                        <div class="flex items-center justify-between">
                                            {{ expense.type.name }}
                                            <div class="ml-3 flex items-center">
                                                <button v-if="canUpdateProposal" type="button" @click="openEditExpense(expense.id)">
                                                    <inline-svg name="edit-pencil" classes="w-4 h-4 cursor-pointer"></inline-svg>
                                                </button>
                                                <button v-if="canDeleteExpense" type="button" @click="confirmDeleteExpense(expense.id)">
                                                    <inline-svg name="trash" classes="ml-2 mb-0.5 w-3 h-3 text-red-500 hover:text-red-300 fill-current cursor-pointer"></inline-svg>
                                                </button>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <button v-if="canUpdateProposal" type="button" @click="openEditEscalations(expense.id)" class="mx-auto flex items-center btn btn-small btn-blue">
                                            <inline-svg name="edit" classes="w-3 h-3 text-white fill-current cursor-pointer"></inline-svg>
                                            <span class="ml-2 text-xs">Escalations</span>
                                        </button>                                        
                                    </td>
                                    <td class="text-right" v-for="(i, iIndex) in selectedProposal.term" :key="iIndex">
                                        {{ getEscalationForYear(expense, i) }}
                                    </td>
                                    <td class="bg-blue-500"></td>
                                </tr>
                                <tr>
                                    <td></td>
                                    <td>
                                        <div class="flex items-center justify-between">
                                            <span>{{ expense.base_net_included }}</span>
                                            <span class="ml-5">{{ formatCurrency(expense.amount, expense.frequency == 'PSF' ? 2 : 0) }}</span>
                                        </div>
                                    </td>
                                    <td class="text-right" v-for="(row, rowIndex) in expense.cost_by_term_year" :key="rowIndex">
                                        {{ formatCurrency(row.total, 0) }}
                                    </td>
                                    <td class="bg-blue-500 text-white text-right font-semibold">
                                        {{ formatCurrency(getExpenseTotal(expense), 0) }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <button v-if="canDeleteProposal" class="mt-3 flex self-end btn btn-red" @click.prevent="confirmDeleteProposal">
                        <div class="flex items-center">
                            <inline-svg name="trash" classes="w-3 h-3 text-white fill-current" />
                            <span class="ml-2">Delete Proposal</span>
                        </div>
                    </button>
                </div>
                
                <modal name="new-proposal-expense" height="auto" click-to-close draggable=".modal-header">
                    <new-proposal-expense @save-proposal="saveProposal"></new-proposal-expense>
                </modal>

                <modal name="edit-proposal-expense" height="auto" click-to-close draggable=".modal-header">
                    <edit-proposal-expense @save-proposal="saveProposal"></edit-proposal-expense>
                </modal>

                <modal name="edit-proposal-expense-escalations" width="1100px" height="auto" click-to-close draggable=".modal-header">
                    <edit-proposal-expense-escalations @save-proposal="saveProposal"></edit-proposal-expense-escalations>
                </modal>

                <notifications classes="notification" position="bottom right"></notifications>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapGetters, mapState } from 'vuex';
    
    export default {
        name: 'ReoptProposal',

        props: ['data'],

        data() {
            return { 
                termData: {},
                editing: false,
                loading: false
            }
        },

        created() {
            this.proposals.all.push(this.data);
            this.proposals.filtered.push(this.data);
            this.proposals.selectedProposalId = this.data.id;

            _.map(this.data.expenses, expense => {
                expense.cost_by_term_year = JSON.parse(expense.cost_by_term_year);
            });

            let rent = _.find(this.selectedProposal.expenses, ['type_id', 1]);
            this.termData = _.map([...Array(parseInt(this.data.term)).keys()], year => {
                let numberFreeMonths = 0;

                if (rent) {
                    numberFreeMonths = _.filter(rent.free_months, ['year_number', year + 1]).length;
                }

                return {
                    'year': year + 1,
                    'number_free_months': numberFreeMonths,
                    'total': 0
                }
            });

            this.recalculateYearlyTotals();
        },

        watch: {
            'selectedProposal.expenses': {
                handler(newValue, oldValue) {
                    this.recalculateYearlyTotals();
                },
                deep: true
            }
        },

        computed: {
            ...mapState(['projects']),
            ...mapState(['proposals']),
            ...mapGetters(['selectedProposal']),

            canCreateRevision() {
                return this.$gate.can('create', 'project');
            },

            canUpdateProposal() {
                return this.$gate.can('update', 'project', this.selectedProposal.project_id);
            },

            canDeleteProposal() {
                return this.$gate.can('delete', 'project', this.selectedProposal.project_id);
            },

            canCreateExpense() {
                return this.$gate.can('create', 'project');
            },
            canDeleteExpense() {
                return this.$gate.can('delete', 'project', this.selectedProposal.project_id);
            },

            usableSqFt() {
                let rentable = Number.prototype.cleanseInteger(this.selectedProposal.total_square_feet);
                let lossFactor = Number.prototype.cleanseInteger(this.selectedProposal.loss_factor_percentage);

                return rentable - (rentable * (lossFactor / 100));
            },

            baseRent() {
                let rent = _.find(this.selectedProposal.expenses, ['type_id', 1]);

                return rent?.amount || 0;
            },

            expenses() {
                return _.sortBy(this.selectedProposal.expenses, [expense => { return expense.type.name != 'Rent'; }]);
            },

            monthsInTerm() {
                let termYears = parseInt(this.selectedProposal.term);

                if (Number.isInteger(termYears)) {
                    return (termYears * 12) + (this.selectedProposal.include_free_months ? 0 : this.totalNumberFreeMonths);
                }

                return (this.selectedProposal.include_free_months ? 0 : this.totalNumberFreeMonths);
            },

            totalNumberFreeMonths() {
                return _.sumBy(this.termData, row => parseInt(row.number_free_months));
            },

            totalRent() {
                let rent = _.find(this.selectedProposal.expenses, ['type_id', 1]);

                if (rent) {
                    return _.map(rent.cost_by_term_year, row => row.total).reduce((partialSum, a) => partialSum + a, 0);
                }

                return 0;
            },

            totalOperatingExpenses() {
                let expenses = _.filter(this.selectedProposal.expenses, expense => expense.type_id != 1);

                if (expenses) {
                    return _.flatMap(expenses, expense => {
                        return _.map(expense.cost_by_term_year, row => row.total)
                    }).reduce((partialSum, a) => partialSum + a, 0);
                }

                return 0;
            },

            totalLeaseCost() {
                return this.totalRent + this.totalOperatingExpenses;
            },

            npv() {
                let npv = 0;
                let discount_rate = Number.prototype.cleanseInteger(this.selectedProposal.discount_rate) / 100;                
                
                this.termData.forEach(row => {
                    npv += row.total / (Math.pow(1 + discount_rate, row.year + 1));
                });

                return npv;
            },

            tiToAmortization() {
                return ( Number.prototype.cleanseFloat(this.selectedProposal.ti_required_psf) -  Number.prototype.cleanseFloat(this.selectedProposal.ti_offered_psf)) * 100;
            },

            tiTenantCost() {
                return this.selectedProposal.total_square_feet * this.tiToAmortization;
            },

            annualAverageCost() {
                if (this.selectedProposal.term == 0) {
                    return 0;
                }
                
                return this.totalLeaseCost / this.selectedProposal.term;
            },

            monthlyAverageCost() {
                if (this.selectedProposal.monthsInTerm == 0) {
                    return 0;
                }
                
                return this.totalLeaseCost / this.monthsInTerm;
            },

            effectiveRentPSF() {
                if (this.selectedProposal.term == 0) {
                    return 0;
                }
                
                return (this.totalLeaseCost / this.selectedProposal.term) / parseInt(this.selectedProposal.total_square_feet);
            },

            averagePUSF() {
                if (this.usableSqFt == 0 || this.monthsInTerm == 0) {
                    return 0;
                }
                
                return (this.totalLeaseCost / (this.monthsInTerm / 12)) / parseInt(this.usableSqFt);
            }
        },

        methods: {
            recalculateYearlyTotals() {
                this.termData.forEach(row => {
                    row.total = _.map(this.selectedProposal.expenses, expense => {
                        return expense.cost_by_term_year[row.year].total;
                    }).reduce((partialSum, a) => partialSum + a, 0);
                });
            },

            getEscalationForYear(expense, yearNumber) {
                let escalation = _.find(expense.escalations, ['year_number', yearNumber]);

                return escalation?.amount || null;
            },

            getExpenseTotal(expense) {
                return _.map(expense.cost_by_term_year, row => {
                    return row.total;
                }).reduce((partialSum, a) => partialSum + a, 0);
            },

            openEditExpense(expenseId) {
                this.proposals.selectedProposalExpenseId = expenseId;

                this.$modal.show('edit-proposal-expense');
            },

            openEditEscalations(expenseId) {
                this.proposals.selectedProposalExpenseId = expenseId;

                this.$modal.show('edit-proposal-expense-escalations');
            },

            async saveProposal() {
                this.loading = true;

                this.selectedProposal.total_lease_cost = this.totalLeaseCost;
                this.selectedProposal.effective_rent_prsf = this.effectiveRentPSF;
                this.selectedProposal.average_rent_pusf = this.averagePUSF;
                this.selectedProposal.average_monthly_cost = this.monthlyAverageCost;
                this.selectedProposal.npv = this.npv;

                await this.$store.dispatch('saveProposal').then(() => {
                    this.loading = false;

                    this.$notify({
                        type: 'success',
                        title: 'Success',
                        text: "Proposal Saved"
                    });
                });
            },

            confirmDeleteExpense(expenseId) {
                this.$swal({
                    title: 'Delete this expense?',
                    text: "ALL data will be lost",
                    type: 'warning',
                    buttonsStyling: false,
                    confirmButtonText: 'Yes - Delete it',
                    confirmButtonClass: 'btn btn-red',
                    showCancelButton: true,
                    cancelButtonClass: 'btn btn-secondary mr-4',
                    reverseButtons: true
                }).then((result) => {
                    if (result.value) {
                        this.$store.dispatch('deleteExpense', expenseId).then(() => {
                            this.saveProposal();
                        });
                    }
                });
            },

            async saveFreeMonths() {
                let rent = _.find(this.selectedProposal.expenses, ['type_id', 1]);

                if (rent) {
                    rent.free_months = [];

                    this.termData.forEach(row => {
                        let freeMonthsInput = document.querySelector(`select[data-year='${row.year}']`);
                        let numberFreeMonths = freeMonthsInput.value;

                        _.each([...Array(parseInt(numberFreeMonths)).keys()], month => {
                            rent.free_months.push({
                                'expense_id': rent.id,
                                'year_number': row.year,
                                'month_number': month + 1
                            });
                        });
                    });

                    this.proposals.selectedProposalExpenseId = rent.id;

                    await this.$store.dispatch('saveExpense').then(() => {
                        this.$notify({
                            type: 'success',
                            title: 'Success',
                            text: "Free Months Saved"
                        });

                        this.saveProposal();
                    });
                }
            },

            async createRevision() {
                this.loading = true;

                await this.$store.dispatch('createRevision').then(() => {
                    this.loading = false;

                    this.$notify({
                        type: 'success',
                        title: 'Success',
                        text: "Revision Created"
                    });
                });
            },

            confirmDeleteProposal() {
                this.$swal({
                    title: 'Delete this proposal?',
                    text: "ALL data will be lost",
                    type: 'warning',
                    buttonsStyling: false,
                    confirmButtonText: 'Yes - Delete it',
                    confirmButtonClass: 'btn btn-red',
                    showCancelButton: true,
                    cancelButtonClass: 'btn btn-secondary mr-4',
                    reverseButtons: true
                }).then(async (result) => {
                    if (result.value) {
                        await this.$store.dispatch('deleteProposal').then(() => {
                            this.$notify({
                                type: 'success',
                                title: 'Proposal Deleted',
                                text: "Redirecting..."
                            });

                            setTimeout(() => {
                                window.location = '/projects/' + this.selectedProposal.project_id
                            }, 1300);
                        });
                    }
                });
            },
        }
    }
</script>